import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { TranslocoService } from '@ngneat/transloco';
import { BundleService } from '../../../../../services/bundles';

type BundlePrice = number | string | null;

interface BundleStock {
  id?: number;
  name_en?: string;
  name_ar?: string;
  image?: string;
}

interface BundleItem {
  id?: number;
  bundle_id?: number;
  stock_id?: number;
  quantity?: number;
  stock?: BundleStock | null;
}

interface BundleMobileItem {
  id: number;
  name_en?: string;
  name_ar?: string;
  image?: string;

  price?: BundlePrice;
  affiliationprice?: BundlePrice;
  pricea?: BundlePrice;
  priceb?: BundlePrice;
  pricec?: BundlePrice;

  items?: BundleItem[];
}

@Component({
  selector: 'app-allbundlesmobile',
  templateUrl: './allbundlesmobile.component.html',
  styleUrl: './allbundlesmobile.component.scss'
})
export class AllbundlesmobileComponent implements OnInit {
  bundles: BundleMobileItem[] = [];
  filteredBundles: BundleMobileItem[] = [];

  totalItems = 0;
  page = 1;
  perPage = 10;

  totalBundles = 0;
  lowestPrice = 0;
  highestPrice = 0;

  contentReady = false;
  isLoadingMore = false;
  noData = false;
  noMoreData = false;

  searchTerm = '';
  isAr = false;

  private readonly imageBaseUrl =
    'https://backendproduction.blueboxpet.com/storage/app/public/';

  constructor(
    private _bundleService: BundleService,
    private router: Router,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.setLanguage();
    this.getBundles(false);
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    const bottomReached =
      scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  getBundles(append = false): void {
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.contentReady = true;
      this.noData = false;
    }

    this._bundleService
      .getAll(this.page, this.perPage)
      .subscribe({
        next: (res: any) => {
          /*
           * الـ API الحالي بيرجع:
           *
           * {
           *   bundles: [...]
           * }
           *
           * والـ fallbacks الموجودة تحت بتخلي الصفحة تشتغل
           * حتى لو شكل الـ response اتغير بعد كده.
           */
          const data = this.extractBundles(res);

          if (append) {
            this.bundles = this.mergeUniqueBundles(
              this.bundles,
              data
            );
          } else {
            this.bundles = data;
          }

          const responseTotal = this.extractTotal(res);

          this.totalItems =
            responseTotal !== null
              ? responseTotal
              : this.bundles.length;

          this.updateStatistics(res);

          this.noData = this.bundles.length === 0;

          /*
           * بعض الـ endpoints بترجع bundles بدون pagination.
           * لذلك لو مفيش معلومات pagination والداتا راجعة
           * مباشرة في res.bundles، بنعتبر إن كل الداتا اتحملت.
           */
          const directBundlesResponse =
            Array.isArray(res?.bundles) &&
            !this.hasPaginationInformation(res);

          this.noMoreData =
            directBundlesResponse ||
            data.length < this.perPage ||
            (
              responseTotal !== null &&
              this.bundles.length >= responseTotal
            );

          this.applyLocalFilter();

          this.contentReady = false;
          this.isLoadingMore = false;
        },

        error: (error) => {
          console.error('Failed to load bundles:', error);

          /*
           * لو الخطأ حصل أثناء تحميل صفحة إضافية،
           * نرجع رقم الصفحة علشان المستخدم يقدر يحاول تاني.
           */
          if (append && this.page > 1) {
            this.page -= 1;
          }

          this.contentReady = false;
          this.isLoadingMore = false;

          /*
           * ما نظهرش No Data لو فيه بيانات محملة بالفعل
           * والخطأ حصل في Load More فقط.
           */
          this.noData = this.bundles.length === 0;
        }
      });
  }

  loadMore(): void {
    if (
      this.contentReady ||
      this.isLoadingMore ||
      this.noMoreData ||
      this.noData
    ) {
      return;
    }

    this.page += 1;
    this.getBundles(true);
  }

  applyFilter(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.searchTerm = input?.value || '';
    this.applyLocalFilter();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyLocalFilter();
  }

  edit(bundle: BundleMobileItem): void {
    this.router.navigate(['/addbundle'], {
      queryParams: bundle
    });
  }

  delete(bundleId: number): void {
    Swal.fire({
      title: this.t('confirmDeleteTitle'),
      text: this.t('confirmDeleteText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.t('yesDelete'),
      cancelButtonText: this.t('cancel'),
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this._bundleService.delete(bundleId).subscribe({
        next: () => {
          Swal.fire(
            this.t('deletedTitle'),
            this.t('deletedText'),
            'success'
          );

          this.reloadFirstPage();
        },

        error: (error) => {
          console.error('Failed to delete bundle:', error);

          Swal.fire(
            this.t('deleteErrorTitle'),
            this.t('deleteErrorText'),
            'error'
          );
        }
      });
    });
  }

  reloadFirstPage(): void {
    this.page = 1;

    this.noData = false;
    this.noMoreData = false;

    this.bundles = [];
    this.filteredBundles = [];

    this.totalItems = 0;
    this.totalBundles = 0;
    this.lowestPrice = 0;
    this.highestPrice = 0;

    this.getBundles(false);
  }

  fixImage(img?: string): string {
    if (!img) {
      return '/assets/no-image.png';
    }

    if (
      img.startsWith('http://') ||
      img.startsWith('https://')
    ) {
      return img;
    }

    return (
      this.imageBaseUrl +
      img.replace(/^\/+/, '')
    );
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;

    if (img.src.includes('/assets/no-image.png')) {
      return;
    }

    img.src = '/assets/no-image.png';
  }

  trackByBundle(
    index: number,
    item: BundleMobileItem
  ): number {
    return item?.id || index;
  }

  private extractBundles(res: any): BundleMobileItem[] {
    /*
     * الشكل الحالي للـ API:
     * { bundles: [...] }
     */
    if (Array.isArray(res?.bundles)) {
      return res.bundles;
    }

    /*
     * شكل شائع:
     * { data: [...] }
     */
    if (Array.isArray(res?.data)) {
      return res.data;
    }

    /*
     * أشكال pagination محتملة:
     * { data: { data: [...] } }
     * { data: { bundles: [...] } }
     */
    if (Array.isArray(res?.data?.data)) {
      return res.data.data;
    }

    if (Array.isArray(res?.data?.bundles)) {
      return res.data.bundles;
    }

    /*
     * لو الـ API رجع array مباشرة.
     */
    if (Array.isArray(res)) {
      return res;
    }

    return [];
  }

  private extractTotal(res: any): number | null {
    const rawTotal =
      res?.total ??
      res?.meta?.total ??
      res?.pagination?.total ??
      res?.data?.total ??
      res?.data?.meta?.total ??
      res?.data?.pagination?.total ??
      null;

    if (
      rawTotal === null ||
      rawTotal === undefined ||
      rawTotal === ''
    ) {
      return null;
    }

    const total = Number(rawTotal);

    return Number.isFinite(total)
      ? total
      : null;
  }

  private hasPaginationInformation(res: any): boolean {
    return Boolean(
      res?.total !== undefined ||
      res?.current_page !== undefined ||
      res?.last_page !== undefined ||
      res?.next_page_url !== undefined ||
      res?.meta ||
      res?.pagination ||
      res?.data?.total !== undefined ||
      res?.data?.current_page !== undefined ||
      res?.data?.last_page !== undefined ||
      res?.data?.meta ||
      res?.data?.pagination
    );
  }

  private mergeUniqueBundles(
    currentBundles: BundleMobileItem[],
    newBundles: BundleMobileItem[]
  ): BundleMobileItem[] {
    const bundlesMap = new Map<number, BundleMobileItem>();

    currentBundles.forEach((bundle) => {
      bundlesMap.set(bundle.id, bundle);
    });

    newBundles.forEach((bundle) => {
      bundlesMap.set(bundle.id, bundle);
    });

    return Array.from(bundlesMap.values());
  }

  private updateStatistics(res: any): void {
    const stats =
      res?.stats ??
      res?.data?.stats ??
      null;

    const apiTotalBundles = this.toNumberOrNull(
      stats?.total_bundles ??
      stats?.totalBundles ??
      res?.total_bundles
    );

    const apiLowestPrice = this.toNumberOrNull(
      stats?.lowest_price ??
      stats?.lowestPrice ??
      res?.lowest_price
    );

    const apiHighestPrice = this.toNumberOrNull(
      stats?.highest_price ??
      stats?.highestPrice ??
      res?.highest_price
    );

    const prices = this.bundles
      .map((bundle) => this.toNumberOrNull(bundle.price))
      .filter(
        (price): price is number =>
          price !== null
      );

    this.totalBundles =
      apiTotalBundles ??
      this.totalItems ??
      this.bundles.length;

    this.lowestPrice =
      apiLowestPrice ??
      (
        prices.length > 0
          ? Math.min(...prices)
          : 0
      );

    this.highestPrice =
      apiHighestPrice ??
      (
        prices.length > 0
          ? Math.max(...prices)
          : 0
      );
  }

  private toNumberOrNull(
    value: unknown
  ): number | null {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return null;
    }

    const numberValue = Number(value);

    return Number.isFinite(numberValue)
      ? numberValue
      : null;
  }

  private applyLocalFilter(): void {
    const value =
      this.searchTerm
        .trim()
        .toLowerCase();

    if (!value) {
      this.filteredBundles = [...this.bundles];
      return;
    }

    this.filteredBundles =
      this.bundles.filter((bundle) => {
        const nameEn =
          bundle.name_en
            ?.toLowerCase() ||
          '';

        const nameAr =
          bundle.name_ar
            ?.toLowerCase() ||
          '';

        const itemsText =
          bundle.items
            ?.map((item) => {
              const stockNameEn =
                item.stock?.name_en || '';

              const stockNameAr =
                item.stock?.name_ar || '';

              return `${stockNameEn} ${stockNameAr}`;
            })
            .join(' ')
            .toLowerCase() ||
          '';

        return (
          nameEn.includes(value) ||
          nameAr.includes(value) ||
          itemsText.includes(value)
        );
      });
  }

  private setLanguage(): void {
    const storedLang =
      typeof window !== 'undefined'
        ? localStorage.getItem('language')
        : '';

    this.isAr =
      (storedLang || this.transloco.getActiveLang()) ===
      'ar';
  }

  private t(key: string): string {
    return this.transloco.translate(
      `bundles.management.${key}`
    );
  }
}